import * as R from 'ramda';
import {
  put,
  call,
  race,
  take,
  select,
  takeLatest,
} from 'redux-saga/effects';
// common
import {
  initialDataLoadFail,
  initialDataLoadSuccess,
  showRequestStatusModal,
} from '../../common/actions';
// components
import { closeModal } from '../../components/modal/actions';
import { openLoader, closeLoader } from '../../components/loader/actions';
// features
import { getConfigByGroupSaga } from '../configurations/sagas';
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
import { makeSelectAccessToPagesList, makeSelectInitialDataLoadedStatus } from '../permission/selectors';
import {
  getAllAvailableRefTypesByScopeRequest,
} from '../reference/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSagas } from '../../report-common';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature carrier
import * as A from './actions';
import * as S from './selectors';
import {
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectPageVisited,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

export function* handleGetCarrierOnboardingPackageRequest() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.carrierOnboardingPackageList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.receivedCarrierOnboardingPackageSuccess(G.isNotNilAndNotEmpty(data)));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetCarrierOnboardingPackageRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetCarrierOnboardingPackageRequest exception');
  }
}

function* handleCreateCarrierSaferWatchMassUpdateByReportRequestSaga({ payload }) {
  try {
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.CURRENT_BRANCH, currentBranchGuid, payload),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.carrierSaferWatchMassUpdateByReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateCarrierSaferWatchMassUpdateByReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleCreateCarrierSaferWatchMassUpdateByReportRequestSaga exception');
  }
}

function* handleCreateCarrierSaferWatchMassUpdateRequestSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: payload,
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.carrierSaferWatchMassUpdate, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      if (G.isNotNilAndNotEmpty(data.invalidCarriers)) {
        if (R.gt(data.requestedToUpdate, data.updated)) {
          const updatedCarriersMessage = R.join(
            ' ',
            [
              G.getWindowLocale('titles:updated', 'Updated'),
              data.updated,
              G.getWindowLocale('titles:of', 'of'),
              data.requestedToUpdate,
              G.getWindowLocale('titles:carriers', 'Carriers'),
            ],
          );

          yield call(G.showToastrMessage, 'success', updatedCarriersMessage);
        }

        R.forEach(
          ({ carrierName, errorMessage }) =>
            G.showToastrMessageSimple('error', `${carrierName}: ${errorMessage}`),
          data.invalidCarriers,
        );
      } else {
        yield call(G.showToastrMessage, 'success', 'toastr:success:200');
      }
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateCarrierSaferWatchMassUpdateRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleCreateCarrierSaferWatchMassUpdateRequestSaga exception');
  }
}

export function* createSendOnboardingPackageRequestSaga({ payload }) {
  try {
    const { editMode, carrierGuid } = payload;

    if (editMode) yield put(openLoader());

    const options = {
      params: { carrierGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.sendOnboardingPackage, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      if (editMode) {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'createSendOnboardingPackageRequestSaga fail');
    }

    if (editMode) yield put(closeLoader());
  } catch (error) {
    if (payload.editMode) yield put(closeLoader());

    yield call(G.handleException, error, 'createSendOnboardingPackageRequestSaga exception');
  }
}

export function* handleCreateCarrierSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const reqData = {
      ...payload,
      [GC.FIELD_BRANCH_GUID]: branchGuid,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.carrier, { data: reqData });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { guid } = data;

      yield put(closeModal());

      yield call(G.goToCarrierProfile, guid);

      if (R.prop(GC.FIELD_CARRIER_SEND_PACKAGE, payload)) {
        yield put(A.createSendOnboardingPackageRequest({ editMode: false, carrierGuid: guid }));
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateCarrierSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleCreateCarrierSaga exception');
  }
}

function* handleCreateCarrierRefSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const options = {
      data: payload,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.carrierReference, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateCarrierRefSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleCreateCarrierRefSaga exception');
  }
}

export function* handleDeleteItemSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'delete', endpointsMap.getCarrierEndpoint(payload));

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'toastr:success:204');

      yield put(A.deleteItemSuccess(payload));
      yield put(closeModal());
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteItemSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleDeleteItemSaga exception');
  }
}

export function* handleGetItemListSaga({ payload }) {
  try {
    if (G.isTrue(G.getPropFromObject('openLoader', payload))) {
      yield put(openLoader({ showDimmer: true }));
    }

    yield put(A.setListLoading(true));

    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const pagination = yield select(makeSelectPagination());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const guids = R.pathOr(null, ['guids'], payload);

    let fields = G.getOrElse(reportParams, 'fields', []);

    let requiredFields = [
      { name: GC.FIELD_NAME, sequence: 100, reference: false },
      { name: GC.FIELD_BRANCH_GUID, sequence: 101, reference: false },
    ];

    if (G.isTrue(reportParams.showIssues)) {
      const issueFields = [
        { name: GC.FIELD_ISSUES_COUNT, freezed: false, sequence: 201, reference: false },
        { name: GC.FIELD_MAX_ISSUE_WARNING_LEVEL, freezed: false, sequence: 202, reference: false },
        { name: GC.FIELD_ISSUES, freezed: false, sequence: 200, reference: false, collection: true },
      ];

      requiredFields = R.concat(requiredFields, issueFields);
    }

    requiredFields.forEach((field) => {
      if (R.not(R.any(R.propEq(field.name, 'name'), fields))) {
        fields = R.concat(
          fields,
          R.of(Array, field),
        );
      }
    });

    const { limit, offset } = pagination;

    const reqBody = {
      guids,
      limit,
      offset,
      fields,
      orderFields,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const res = yield call(
      sendRequest,
      'post',
      endpointsMap.carrierList,
      { data: reqBody },
    );

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data, guids }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }

    yield put(A.setListLoading(false));
    yield put(closeLoader());
  } catch (err) {
    yield call(G.handleException, err, 'handleGetItemListSaga exception');

    yield put(A.setListLoading(false));
    yield put(closeLoader());
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(
  GC.CARRIER_REPORT,
  A,
  handleGetItemListSaga,
  { S, createUpdateReportSuccessCallback },
);

function* handleExportReportDataSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const currentEnterprise = yield select(makeSelectCurrentBranchGuid());

    const { fields, fileType, orderFields, searchCriteria } = payload;

    const reqBody = {
      fields,
      orderFields,
      searchCriteria,
      currentEnterprise,
      [GC.FIELD_REPORT_NAME]: G.getPropFromObject(GC.FIELD_NAME, payload),
    };

    const params = { format: fileType };

    const options = {
      params,
      data: reqBody,
      resType: 'arraybuffer',
    };

    const res = yield call(sendRequest, 'post', endpointsMap.carrierExportReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'handleExportReportDataSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleExportReportDataSaga exception');
  }
}

export function* handleCreateWatchRequestSaga({ payload }) {
  try {
    const { watch, hideMessage, usDotNumber } = payload;

    const endpointName = G.ifElse(
      watch,
      'addToWatchList',
      'removeFromWatchList',
    );

    if (R.and(watch, G.isNilOrEmpty(usDotNumber))) {
      return yield call(G.showToastrMessage, 'error', 'validation:usdot-number');
    }

    yield put(openLoader());

    const endpoint = R.prop(endpointName, endpointsMap);

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        usDotNumber,
        [GC.BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      if (R.not(hideMessage)) {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateWatchRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleCreateWatchRequestSaga exeption');
  }
}

function* handleAddCarriersToWatchListRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: payload,
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.addAllToWatchList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      if (G.isNotNilAndNotEmpty(data.invalidCarriesNames)) {
        const carrierNames = R.join(', ', data.invalidCarriesNames);
        const message = `${G.getWindowLocale('titles:invalid-carriers', 'Invalid Carriers')}: ${carrierNames}`;

        yield call(G.showToastrMessage, 'error', message);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleAddCarriersToWatchListRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleAddCarriersToWatchListRequestSaga exeption');
  }
}

function* handleAddCarriersToWatchListByReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = { data: R.assoc(GC.CURRENT_BRANCH, branchGuid, payload) };

    const res = yield call(sendRequest, 'post', endpointsMap.addToWatchListByReport, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      if (G.isNotNilAndNotEmpty(data.invalidCarriesNames)) {
        const carrierNames = R.join(', ', data.invalidCarriesNames);
        const message = `${G.getWindowLocale('titles:invalid-carriers', 'Invalid Carriers')}: ${carrierNames}`;

        yield call(G.showToastrMessage, 'error', message);

        yield put(A.resetListAndPagination());
        yield put(A.getItemListRequest({ openLoader: true }));
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleAddCarriersToWatchListByReportRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleAddCarriersToWatchListByReportRequestSaga exeption');
  }
}

function* changeBranchRequest({ payload }) {
  try {
    yield put(openLoader());

    const options = { data: payload };

    const res = yield call(sendRequest, 'put', endpointsMap.carrierChangeBranch, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(closeModal());
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'changeBranchRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'changeBranchRequest exception');
  }
}

// carrier load board synchronization
export function* addToNetworkCarriersRequest({ payload }) {
  try {
    const { source, carrierGuids } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: carrierGuids,
      params: { source, [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.addToNetwork, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      if (G.isNotNilAndNotEmpty(data)) {
        const failedCarriersString = R.compose(
          R.join(', '),
          R.map(({ name, errorMessage }) => `${name} - ${errorMessage}`),
        )(data);

        yield call(G.showToastrMessage, 'error', failedCarriersString);
      } else {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'addToNetworkCarriersRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'addToNetworkCarriersRequest exeption');
  }
}

function* addToNetworkCarriersByReportRequest({ payload }) {
  try {
    const { source, fields, orderFields, searchCriteria } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { source },
      data: { fields, orderFields, searchCriteria, [GC.CURRENT_BRANCH]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.addToNetworkByReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'addToNetworkCarriersByReportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'addToNetworkCarriersByReportRequest exeption');
  }
}

function* allowBookItNowCarriersRequest({ payload }) {
  try {
    const { source, carrierGuids } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: carrierGuids,
      params: { source, [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.allowBookItNow, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      if (G.isNotNilAndNotEmpty(data)) {
        const failedCarriersString = R.compose(
          R.join(', '),
          R.map(({ name, errorMessage }) => `${name} - ${errorMessage}`),
        )(data);

        yield call(G.showToastrMessage, 'error', failedCarriersString);
      } else {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'allowBookItNowCarriersRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'allowBookItNowCarriersRequest exeption');
  }
}

function* allowBookItNowCarriersByReportRequest({ payload }) {
  try {
    const { source, fields, orderFields, searchCriteria } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: { source },
      data: { fields, orderFields, searchCriteria, [GC.CURRENT_BRANCH]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.allowBookItNowByReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'allowBookItNowCarriersByReportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'allowBookItNowCarriersByReportRequest exeption');
  }
}

function* forbidBookItNowCarriersRequest({ payload }) {
  try {
    const { source, carrierGuids } = payload;

    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: carrierGuids,
      params: { source, [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.forbidBookItNow, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      if (G.isNotNilAndNotEmpty(data)) {
        const failedCarriersString = R.compose(
          R.join(', '),
          R.map(({ name, errorMessage }) => `${name} - ${errorMessage}`),
        )(data);

        yield call(G.showToastrMessage, 'error', failedCarriersString);
      } else {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'forbidBookItNowCarriersRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'forbidBookItNowCarriersRequest exeption');
  }
}

function* removeFromNetworkCarriersRequest({ payload }) {
  try {
    const { source, carrierGuids } = payload;

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    yield put(openLoader());

    const options = {
      data: carrierGuids,
      params: { source, [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.removeFromNetwork, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      if (G.isNotNilAndNotEmpty(data)) {
        const failedCarriersString = R.compose(
          R.join(', '),
          R.map(({ name, errorMessage }) => `${name} - ${errorMessage}`),
        )(data);

        yield call(G.showToastrMessage, 'error', failedCarriersString);
      } else {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'removeFromNetworkCarriersRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'removeFromNetworkCarriersRequest exeption');
  }
}

// carrier synchronization
function* syncCarrierByReportRequest({ payload }) {
  try {
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: R.assoc(GC.CURRENT_BRANCH, currentBranchGuid, payload),
    };

    const res = yield call(sendRequest, 'put', endpointsMap.syncCarrierByReport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
    } else {
      yield call(G.handleFailResponse, res, 'syncCarrierByReportRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'syncCarrierByReportRequest exception');
  }
}

export function* syncCarrierListRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: payload,
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.syncSelectedCarriers, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      if (G.isNotNilAndNotEmpty(data.invalidCarriers)) {
        if (R.gt(data.requestedToUpdate, data.updated)) {
          const updatedCarriersMessage = R.join(
            ' ',
            [
              G.getWindowLocale('titles:updated', 'Updated'),
              data.updated,
              G.getWindowLocale('titles:of', 'of'),
              data.requestedToUpdate,
              G.getWindowLocale('titles:carriers', 'Carriers'),
            ],
          );

          yield call(G.showToastrMessage, 'success', updatedCarriersMessage);
        }

        R.forEach(
          ({ carrierName, errorMessage }) =>
            G.showToastrMessageSimple('error', `${carrierName}: ${errorMessage}`),
          data.invalidCarriers,
        );
      } else {
        yield call(G.showToastrMessage, 'success', 'toastr:success:200');
      }
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
    } else {
      yield call(G.handleFailResponse, res, 'syncCarrierListRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'syncCarrierListRequest exception');
  }
}

function* addSyncCarriersToWatchListRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      data: payload,
      params: { [GC.BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.carrierSynchronizationAddAllToWatchList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));

      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      if (G.isNotNilAndNotEmpty(data.invalidCarriesNames)) {
        const carrierNames = R.join(', ', data.invalidCarriesNames);
        const message = `${G.getWindowLocale('titles:invalid-carriers', 'Invalid Carriers')}: ${carrierNames}`;

        yield call(G.showToastrMessage, 'error', message);
      }
    } else {
      yield call(G.handleFailResponse, res, 'addSyncCarriersToWatchListRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'addSyncCarriersToWatchListRequest exeption');
  }
}

function* addSyncCarriersToWatchListByReportRequest({ payload }) {
  try {
    yield put(openLoader());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = { data: R.assoc(GC.CURRENT_BRANCH, branchGuid, payload) };

    const res = yield call(sendRequest, 'put', endpointsMap.carrierSynchronizationAddToWatchListByReport, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      if (G.isNotNilAndNotEmpty(data.invalidCarriesNames)) {
        const carrierNames = R.join(', ', data.invalidCarriesNames);
        const message = `${G.getWindowLocale('titles:invalid-carriers', 'Invalid Carriers')}: ${carrierNames}`;

        yield call(G.showToastrMessage, 'error', message);

        yield put(A.resetListAndPagination());
        yield put(A.getItemListRequest({ openLoader: true }));
      }
    } else {
      yield call(G.handleFailResponse, res, 'addSyncCarriersToWatchListByReportRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'addSyncCarriersToWatchListByReportRequest exeption');
  }
}

function* addOrRemoveCarrierWatchRequest({ payload }) {
  try {
    const { guid, watch, hideMessage } = payload;

    const endpointName = G.ifElse(
      watch,
      'carrierSynchronizationAddToWatchList',
      'carrierSynchronizationRemoveFromWatchList',
    );

    yield put(openLoader());

    const endpoint = R.prop(endpointName, endpointsMap);

    const options = {
      params: {
        [GC.FIELD_CARRIER_GUID]: guid,
      },
    };

    const res = yield call(sendRequest, 'put', endpoint, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      if (R.not(hideMessage)) {
        yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      }
    } else {
      yield call(G.handleFailResponse, res, 'addOrRemoveCarrierWatchRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'addOrRemoveCarrierWatchRequest exeption');
  }
}

// carrier integration
export function* sendCarrierListToIntegrationRequest({ payload }) {
  try {
    const { carrierGuids, integrationType } = payload;

    yield put(openLoader());
    const options = {
      data: carrierGuids,
      params: { integrationType },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.sendCarrierListToIntegration, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const { totalCount, successCount, sentToDivisionNames } = data;

      yield put(A.sendCarrierListToIntegrationSuccess(sentToDivisionNames));
      yield put(A.resetListAndPagination());
      yield put(A.getItemListRequest({ openLoader: true }));
      yield put(closeModal());

      const errors = R.compose(
        R.values,
        R.mapObjIndexed((messageArray, key) => ({ key, messageArray })),
        R.pathOr({}, ['errors']),
      )(data);

      const title = `${G.getWindowLocale('titles:accounting', 'Accounting')} ${
        G.getWindowLocale('titles:export-result', 'Export Result')}`;

      yield put(showRequestStatusModal({
        title,
        errors,
        totalCount,
        successCount,
        status: G.getPropFromObject(GC.FIELD_STATUS, data),
      }));
    } else {
      yield call(G.handleFailResponse, res, 'sendCarrierListToIntegrationRequest fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'sendCarrierListToIntegrationRequest exception');
  }
}

function* handlerVisitListOfCarriersPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    try {
      yield put(openLoader({ showDimmer: true }));

      const route = payload.pathname;

      const pageVisited = yield select(makeSelectPageVisited());
      const accessToPagesList = yield select(makeSelectAccessToPagesList());
      const isInitialDataLoaded = yield select(makeSelectInitialDataLoadedStatus());

      const isAllowed = R.includes(route, accessToPagesList);

      if (R.not(isInitialDataLoaded)) {
        yield race({
          success: take(initialDataLoadSuccess),
          fail: take(initialDataLoadFail),
        });
      }

      if (R.not(isAllowed)) {
        yield put({ type: GC.CHECK_VISIT_CARRIER_LIST_PAGE, payload: route });

        yield take(GC.CHECK_VISIT_CARRIER_LIST_PAGE_ALLOWED);
      }

      yield put(A.resetListAndPagination());

      yield call(getConfigByGroupSaga, GC.GENERAL_CONFIG_GROUP);

      yield put(A.setIgnorePromptStatus(false));
      yield put(getAllAvailableRefTypesByScopeRequest(GC.REF_SCOPE_NAME_CARRIER));
      yield put(A.setReportPending());
      yield put(A.getDispatchIntegrationConfigListRequest());

      yield call(handleAvailableReportsRequest, { payload, notSetUsedReport: pageVisited });
      yield call(handleGetItemListSaga, { payload: { openLoader: true } });
      yield call(handleGetCarrierOnboardingPackageRequest);
    } catch (error) {
      yield call(G.handleException, error, 'handlerVisitListOfCarriersPageSaga exception');
    } finally {
      yield put(closeLoader());
    }

    break;
  }
}

// carrier tracking synchronization
export function* addCarrierListToTrackingWishListRequest({ payload }) {
  try {
    yield put(openLoader());

    const { guids } = payload;

    let options = {
      data: payload,
      params: { [GC.FIELD_INTEGRATION_TYPE]: GC.DISPATCH_INTEGRATION_TYPE_MACRO_POINT },
    };

    if (G.isNilOrEmpty(guids)) {
      const report = yield select(makeSelectUsedReport());

      const searchCriteria = transformSearchCriteriaBeforeReportPost(R.propOr([], 'searchCriteria', report));

      const reqBody = {
        ...payload,
        searchCriteria,
      };

      options = {
        data: G.setSearchCriteria({ reqBody }),
        params: { [GC.FIELD_INTEGRATION_TYPE]: GC.DISPATCH_INTEGRATION_TYPE_MACRO_POINT },
      };
    }

    const res = yield call(sendRequest, 'post', endpointsMap.carrierTrackingSynchronizationAddToWishList, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:save');
    } else {
      yield call(G.handleFailResponse, res, 'addCarrierListToTrackingWishListRequest fail');
    }

    yield put(closeModal());
  } catch (error) {
    yield call(G.handleException, error, 'addCarrierListToTrackingWishListRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

export function* changeTrackingIntegrationStatusRequest({ payload }) {
  try {
    yield put(openLoader());

    const { status, carrierGuids } = payload;

    const options = {
      data: carrierGuids,
      params: { [GC.FIELD_STATUS]: status },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.carrierTrackingIntegrationChangeStatus, options);

    const { status: responseStatus } = res;

    if (G.isResponseSuccess(responseStatus)) {
      yield put(closeModal());

      yield call(G.showToastrMessage, 'success', 'messages:success:save');
    } else {
      yield call(G.handleFailResponse, res, 'changeTrackingIntegrationStatusRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'changeTrackingIntegrationStatusRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

export function* getDispatchIntegrationConfigListRequest() {
  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        [GC.FIELD_BRANCH_GUID]: branchGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.dispatchIntegrationList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getDispatchIntegrationConfigListSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getDispatchIntegrationConfigListRequest fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getDispatchIntegrationConfigListRequest exception');
  }
}

export function* CarrierWatcherSaga() {
  yield takeLatest(A.deleteItemRequest, handleDeleteItemSaga);
  yield takeLatest(A.changeBranchRequest, changeBranchRequest);
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(A.createCarrierRequest, handleCreateCarrierSaga);
  yield takeLatest(A.createWatchRequest, handleCreateWatchRequestSaga);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.createCarrierRefRequest, handleCreateCarrierRefSaga);
  yield takeLatest(A.exportReportDataRequest, handleExportReportDataSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  yield takeLatest(GC.VISIT_CARRIER_LIST_PAGE, handlerVisitListOfCarriersPageSaga);
  yield takeLatest(A.addCarriersToWatchListRequest, handleAddCarriersToWatchListRequestSaga);
  yield takeLatest(A.createSendOnboardingPackageRequest, createSendOnboardingPackageRequestSaga);
  yield takeLatest(A.addCarriersToWatchListByReportRequest, handleAddCarriersToWatchListByReportRequestSaga);
  yield takeLatest(A.createCarrierSaferWatchMassUpdateRequest, handleCreateCarrierSaferWatchMassUpdateRequestSaga);
  yield takeLatest(
    A.createCarrierSaferWatchMassUpdateByReportRequest,
    handleCreateCarrierSaferWatchMassUpdateByReportRequestSaga,
  );
  // carrier load board synchronization
  yield takeLatest(A.addToNetworkCarriersRequest, addToNetworkCarriersRequest);
  yield takeLatest(A.allowBookItNowCarriersRequest, allowBookItNowCarriersRequest);
  yield takeLatest(A.forbidBookItNowCarriersRequest, forbidBookItNowCarriersRequest);
  yield takeLatest(A.removeFromNetworkCarriersRequest, removeFromNetworkCarriersRequest);
  yield takeLatest(A.addToNetworkCarriersByReportRequest, addToNetworkCarriersByReportRequest);
  yield takeLatest(A.allowBookItNowCarriersByReportRequest, allowBookItNowCarriersByReportRequest);
  // carrier synchronization
  yield takeLatest(A.syncCarrierListRequest, syncCarrierListRequest);
  yield takeLatest(A.syncCarrierByReportRequest, syncCarrierByReportRequest);
  yield takeLatest(A.addOrRemoveCarrierWatchRequest, addOrRemoveCarrierWatchRequest);
  yield takeLatest(A.addSyncCarriersToWatchListRequest, addSyncCarriersToWatchListRequest);
  yield takeLatest(A.addSyncCarriersToWatchListByReportRequest, addSyncCarriersToWatchListByReportRequest);
  // carrier integration
  yield takeLatest(A.sendCarrierListToIntegrationRequest, sendCarrierListToIntegrationRequest);
  // carrier tracking synchronization
  yield takeLatest(A.getDispatchIntegrationConfigListRequest, getDispatchIntegrationConfigListRequest);
  yield takeLatest(A.addCarrierListToTrackingWishListRequest, addCarrierListToTrackingWishListRequest);
  yield takeLatest(A.changeTrackingIntegrationStatusRequest, changeTrackingIntegrationStatusRequest);
}

export default CarrierWatcherSaga;
